import { keyframes } from "@emotion/react";
import SearchIcon from "@mui/icons-material/Search";
import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { type SystemStyleObject } from "@mui/system";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";

import { calculateExpectedLoadTimeMs, type LoadEstimateInput } from "@/utils/loadingEstimate";
import { formatLoadEstimateDuration } from "@/utils/time";

type LongRunningDataLoaderVariant = "legacy" | "table" | "focused";

const minimumTableLoaderDurationMs = 3_000;
const focusedGlowSize = 280;
const labelTransitionDurationMs = 8_000;

interface LongRunningDataLoaderProps {
  isLoading: boolean;
  baseLabels: readonly string[];
  loopingLabels?: readonly string[];
  /** Preserves existing callers until they opt into a calibrated estimate. */
  expectedMeanTimeMs?: number;
  /** Uses a fixed request duration instead of deriving one from store or date-window inputs. */
  fixedDurationMs?: number;
  estimate?: LoadEstimateInput;
  estimateLabel?: (duration: string) => React.ReactNode;
  locale?: string;
  /** Changes whenever a new request begins while loading remains true. */
  loadingKey?: string | number;
  variant?: LongRunningDataLoaderVariant;
  /** Variant container overrides for contextual placements such as chart overlays. */
  containerSx?: SystemStyleObject<Theme>;
  children?: React.ReactNode;
}

const naturalProgress = keyframes`
  0% { transform: scaleX(0.02); }
  70% { transform: scaleX(0.75); }
  100% { transform: scaleX(0.92); }
`;
const lateProgress = keyframes`
  from { transform: scaleX(0.92); }
  to { transform: scaleX(0.9999); }
`;
const orbit = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
const counterOrbit = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(-360deg); }
`;
const breathe = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(0.9); opacity: 0.88; }
  50% { transform: translate3d(0, 0, 0) scale(1); opacity: 1; }
`;
const textShine = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
`;
const glow = keyframes`
  0% { transform: scale(0.88); opacity: 0.7; }
  100% { transform: scale(1.08); opacity: 1; }
`;
const logoShimmer = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(200%); }
`;

const craveIconPath =
  "M20.2997 18.3919H20.3008C21.0699 17.9567 21.7583 17.3966 22.3379 16.7403C22.4217 16.6456 22.3988 16.4985 22.2879 16.4366L20.7122 15.5577C21.7474 14.4604 22.366 12.9663 22.316 11.3282C22.2218 8.23807 19.7051 5.71585 16.6114 5.61132C14.7586 5.54892 13.0897 6.33782 11.9644 7.61609C11.0536 8.6515 10.499 10.0073 10.4944 11.4935C10.4897 12.9954 9.35968 14.294 7.86466 14.4614C5.9994 14.6705 4.42627 13.1452 4.55801 11.2964C4.6606 9.85489 5.82287 8.6775 7.26478 8.55529C8.34686 8.46377 9.32219 8.95521 9.91061 9.7488C10.009 9.88141 10.2121 9.84552 10.259 9.68743C10.5214 8.807 10.9724 7.98688 11.5889 7.28586C11.5978 7.27598 11.6077 7.26454 11.6176 7.25466C10.4756 6.15841 8.89363 5.51563 7.16115 5.61808C4.14976 5.79698 1.7294 8.26979 1.6138 11.2803C1.48518 14.6476 4.17892 17.4163 7.52202 17.4163C9.29251 17.4163 10.8807 16.6389 11.9644 15.4079C12.8746 14.373 13.4292 13.0178 13.4344 11.5331C13.4401 9.96305 14.6701 8.6333 16.2401 8.54801C17.9575 8.4544 19.3786 9.81692 19.3786 11.5112C19.3786 12.5747 18.8193 13.5082 17.9773 14.0303C17.5216 14.3153 16.9837 14.4791 16.4067 14.4791C16.0235 14.4791 15.6574 14.4063 15.3215 14.2747C15.2336 14.2404 15.1336 14.2773 15.0851 14.3585L14.6847 15.0278C14.6279 15.1229 14.6607 15.2462 14.7576 15.3003L18.1069 17.1688L20.2992 18.3909H20.3003";

const CraveThinkingMark: React.FC<{ width: number; height: number; sx: SxProps<Theme> }> = ({
  width,
  height,
  sx,
}) => {
  const id = useId().replaceAll(":", "");
  const gradientId = `loader-logo-gradient-${id}`;
  const shimmerId = `loader-logo-shimmer-${id}`;
  const maskId = `loader-logo-mask-${id}`;

  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      aria-hidden="true"
      sx={sx}
      width={width}
      height={height}
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1="1.61015"
          y1="12.0001"
          x2="22.3889"
          y2="12.0001"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#0066ff" />
          <stop offset="1" stopColor="#60efff" />
        </linearGradient>
        <linearGradient id={shimmerId} x1="0" y1="0" x2="1" y2="0.28">
          <stop offset="30%" stopColor="#fff" stopOpacity="0" />
          <stop offset="45%" stopColor="#fff" stopOpacity="0.35" />
          <stop offset="50%" stopColor="#fff" stopOpacity="0.55" />
          <stop offset="55%" stopColor="#fff" stopOpacity="0.35" />
          <stop offset="70%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <mask id={maskId}>
          <path fill="#fff" d={craveIconPath} />
        </mask>
      </defs>
      <path fill={`url(#${gradientId})`} d={craveIconPath} />
      <g mask={`url(#${maskId})`}>
        <rect
          data-loader-logo-shimmer
          x="-28"
          y="-4"
          width="28"
          height="32"
          fill={`url(#${shimmerId})`}
        />
      </g>
    </Box>
  );
};

export const LongRunningDataLoader: React.FC<LongRunningDataLoaderProps> = ({
  isLoading,
  baseLabels,
  loopingLabels = [],
  expectedMeanTimeMs,
  fixedDurationMs,
  estimate,
  estimateLabel,
  locale,
  loadingKey,
  variant = "legacy",
  containerSx,
  children,
}) => {
  const expectedTimeMs = useMemo(
    () =>
      fixedDurationMs ??
      (estimate ? calculateExpectedLoadTimeMs(estimate) : (expectedMeanTimeMs ?? 0)),
    [estimate, expectedMeanTimeMs, fixedDurationMs],
  );
  const safeExpectedTimeMs = Math.max(1, expectedTimeMs);
  const [ready, setReady] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [labelIndex, setLabelIndex] = useState(0);
  const [shouldAnimateProgress, setShouldAnimateProgress] = useState(isLoading);
  const [frozenProgressTransform, setFrozenProgressTransform] = useState<string>();
  const hasStartedRef = useRef(false);
  const progressRef = useRef<HTMLDivElement>(null);
  const labels = [...baseLabels, ...loopingLabels];
  const label = labels[labelIndex] ?? "";
  const durationLabel = estimateLabel
    ? estimateLabel(formatLoadEstimateDuration(safeExpectedTimeMs, locale))
    : undefined;

  useEffect(() => {
    if (!isLoading) {
      if (variant === "table") return;
      if (!hasStartedRef.current) {
        setReady(true);
        return;
      }
      hasStartedRef.current = false;
      const currentTransform = progressRef.current
        ? window.getComputedStyle(progressRef.current).transform
        : "scaleX(0.02)";
      setFrozenProgressTransform(currentTransform === "none" ? "scaleX(0.02)" : currentTransform);
      setShouldAnimateProgress(false);
      const completionFrame = window.requestAnimationFrame(() => setCompleting(true));
      const timer = window.setTimeout(() => {
        setReady(true);
        setCompleting(false);
      }, 400);
      return () => {
        window.cancelAnimationFrame(completionFrame);
        window.clearTimeout(timer);
      };
    }

    hasStartedRef.current = true;
    setReady(false);
    setCompleting(false);
    setShouldAnimateProgress(true);
    setFrozenProgressTransform(undefined);
    setLabelIndex(0);
    const interval = window.setInterval(() => {
      setLabelIndex((current) => {
        if (current < baseLabels.length - 1) return current + 1;
        if (!loopingLabels.length) return current;
        return baseLabels.length + ((current - baseLabels.length + 1) % loopingLabels.length);
      });
    }, labelTransitionDurationMs);
    return () => window.clearInterval(interval);
  }, [baseLabels.length, isLoading, loadingKey, loopingLabels.length, safeExpectedTimeMs, variant]);

  // Short requests should show the table skeleton directly, without reserving a callout row.
  if (variant === "table" && (!isLoading || expectedTimeMs < minimumTableLoaderDurationMs)) {
    return null;
  }
  if (variant !== "table" && !isLoading && ready) return <>{children}</>;

  const progress = (
    <Box
      aria-label={label}
      role="progressbar"
      sx={{
        height: variant === "table" ? 2 : 4,
        overflow: variant === "focused" ? "hidden" : "visible",
        borderRadius: 2,
        bgcolor: variant === "focused" ? "#eef2f7" : "rgba(0, 102, 255, 0.08)",
      }}
    >
      <Box
        ref={progressRef}
        sx={{
          // Preserve a visible 16px finish gap until the request actually resolves.
          width: completing ? "100%" : "calc(100% - 16px)",
          height: "100%",
          transformOrigin: (theme) => (theme.direction === "rtl" ? "right center" : "left center"),
          background: "/* @noflip */ linear-gradient(90deg, #0066ff, #00b7ff)",
          boxShadow: "0 0 8px rgba(0, 183, 255, 0.55)",
          opacity: 1,
          transform: completing
            ? "scaleX(1)"
            : isLoading && !shouldAnimateProgress
              ? "scaleX(0.02)"
              : frozenProgressTransform,
          transition: completing ? "transform 0.2s linear, width 0.2s linear" : undefined,
          animation: shouldAnimateProgress
            ? `${naturalProgress} ${safeExpectedTimeMs}ms linear forwards, ${lateProgress} ${safeExpectedTimeMs * 3}ms ${safeExpectedTimeMs}ms cubic-bezier(0.16, 1, 0.3, 1) forwards`
            : "none",
        }}
      />
    </Box>
  );

  const commonMotionSx = {
    "@media (prefers-reduced-motion: reduce)": {
      "& *": { animation: "none !important" },
      "& [data-loader-label]": {
        transform: "none !important",
        backgroundImage: "none !important",
        color: "inherit !important",
        transition: "opacity 400ms ease",
      },
    },
  };
  const thinkingMarkSx = {
    position: "relative",
    display: "block",
    willChange: "transform",
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    animation: `${breathe} 2.6s cubic-bezier(0.45, 0, 0.25, 1) infinite`,
    "& [data-loader-logo-shimmer]": {
      transformBox: "fill-box",
      transformOrigin: "0 0",
      animation: `${logoShimmer} 2.6s cubic-bezier(0.45, 0, 0.25, 1) infinite`,
    },
  };
  const labelStack = (
    <>
      <Typography
        component="span"
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          p: 0,
          m: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {label}
      </Typography>
      <Box
        aria-hidden="true"
        sx={{
          position: "relative",
          flex: variant === "focused" ? "0 0 auto" : "1 1 auto",
          minWidth: 0,
          height: variant === "focused" ? 28 : 22,
          fontSize: variant === "focused" ? 18 : 15,
          lineHeight: variant === "focused" ? "28px" : "22px",
          fontWeight: variant === "focused" ? 600 : 500,
          textAlign: variant === "focused" ? "center" : "start",
        }}
      >
        {labels.map((item, index) => (
          <Box
            component="span"
            data-loader-label
            key={`${index}-${item}`}
            sx={{
              position: "absolute",
              inset: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              opacity: index === labelIndex ? 1 : 0,
              transform:
                index === labelIndex
                  ? "translateY(0)"
                  : index < labelIndex
                    ? "translateY(-12px)"
                    : "translateY(12px)",
              willChange: "transform, opacity",
              transition: "opacity 520ms ease-out, transform 640ms cubic-bezier(0.2, 0.8, 0.2, 1)",
              background:
                "/* @noflip */ linear-gradient(90deg, #011638 0%, #011638 42%, #0066ff 50%, #011638 58%, #011638 100%)",
              backgroundSize: "300% 100%",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              animation: index === labelIndex ? `${textShine} 3.9s ease-in-out infinite` : "none",
            }}
          >
            {item}
          </Box>
        ))}
      </Box>
    </>
  );

  if (variant === "table") {
    return (
      <Box
        role="status"
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minHeight: 52,
          px: 2.25,
          overflow: "hidden",
          borderRadius: "12px",
          background:
            "/* @noflip */ linear-gradient(100deg, rgba(0, 102, 255, 0.07), rgba(0, 183, 255, 0.11))",
          ...commonMotionSx,
        }}
      >
        <CraveThinkingMark
          width={34}
          height={34}
          sx={{
            width: 34,
            height: 34,
            flex: "0 0 auto",
            ...thinkingMarkSx,
          }}
        />
        {labelStack}
        {durationLabel ? (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ marginInlineStart: "auto" }}
          >
            {durationLabel}
          </Typography>
        ) : null}
        <Box sx={{ position: "absolute", insetInline: 0, insetBlockEnd: 0, width: "100%" }}>
          {progress}
        </Box>
      </Box>
    );
  }

  if (variant === "focused") {
    return (
      <Box
        role="status"
        sx={{
          position: "relative",
          minHeight: 456,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "12px",
          px: 3,
          background:
            "/* @noflip */ linear-gradient(180deg, rgba(0, 102, 255, 0.03), rgba(0, 183, 255, 0.06))",
          overflow: "hidden",
          ...commonMotionSx,
          ...containerSx,
        }}
      >
        <Box
          component="div"
          data-loader-focused-content
          sx={{
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            pt: 6,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 164,
              height: 108,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: focusedGlowSize,
                height: focusedGlowSize,
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  width: focusedGlowSize,
                  height: focusedGlowSize,
                  borderRadius: "50%",
                  background:
                    "/* @noflip */ radial-gradient(circle, rgba(0, 183, 255, 0.2) 0%, rgba(0, 102, 255, 0.08) 42%, rgba(0, 102, 255, 0) 70%)",
                  animation: `${glow} 5.3s ease-in-out infinite alternate`,
                }}
              />
            </Box>
            <CraveThinkingMark
              width={152}
              height={100}
              sx={{
                width: 152,
                height: 100,
                ...thinkingMarkSx,
                zIndex: 1,
              }}
            />
          </Box>
          <Box sx={{ width: 640, maxWidth: "100%" }}>{labelStack}</Box>
          {durationLabel ? (
            <Typography variant="body2" color="text.secondary">
              {durationLabel}
            </Typography>
          ) : null}
          <Box sx={{ width: 280, maxWidth: "100%" }}>{progress}</Box>
        </Box>
      </Box>
    );
  }

  const legacyProgress = (
    <Box
      aria-label={label}
      role="progressbar"
      sx={{ height: 4, overflow: "hidden", borderRadius: 2, bgcolor: "grey.300" }}
    >
      <Box
        ref={progressRef}
        sx={{
          width: "100%",
          height: "100%",
          transformOrigin: "left center",
          bgcolor: "primary.main",
          transform: completing
            ? "scaleX(1)"
            : isLoading && !shouldAnimateProgress
              ? "scaleX(0.02)"
              : frozenProgressTransform,
          transition: completing ? "transform 0.2s linear" : undefined,
          animation: shouldAnimateProgress
            ? `${naturalProgress} ${safeExpectedTimeMs}ms linear forwards, ${lateProgress} ${safeExpectedTimeMs * 3}ms ${safeExpectedTimeMs}ms cubic-bezier(0.16, 1, 0.3, 1) forwards`
            : "none",
        }}
      />
    </Box>
  );

  return (
    <Box sx={{ mx: "auto", width: "100%", maxWidth: 384, py: 2, color: "text.secondary" }}>
      <Box
        aria-hidden="true"
        sx={{
          position: "relative",
          height: 60,
          mb: 0,
          display: "flex",
          justifyContent: "center",
          ...commonMotionSx,
        }}
      >
        <svg aria-hidden="true" width="0" height="0" focusable="false">
          <defs>
            <linearGradient id="long-running-loader-search-gradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#1a75ff" />
              <stop offset="45%" stopColor="#0066ff" />
              <stop offset="65%" stopColor="#42a5f5" />
              <stop offset="100%" stopColor="#1a75ff" />
              <animateTransform
                attributeName="gradientTransform"
                dur="2.2s"
                repeatCount="indefinite"
                type="translate"
                values="-1 0; 1 0; -1 0"
              />
            </linearGradient>
          </defs>
        </svg>
        <Box
          sx={{
            position: "absolute",
            top: 4,
            left: "calc(50% - 29px)",
            width: 48,
            height: 48,
            animation: `${orbit} 2.8s linear infinite`,
          }}
        >
          <SearchIcon
            sx={{
              position: "absolute",
              top: -5,
              left: 0,
              fontSize: 48,
              animation: `${counterOrbit} 2.8s linear infinite`,
              "& path": { fill: "url(#long-running-loader-search-gradient)" },
            }}
          />
        </Box>
      </Box>
      <Box sx={{ mb: 1, textAlign: "center" }}>{label}</Box>
      {legacyProgress}
    </Box>
  );
};
